import { Injectable, NgZone, inject } from '@angular/core';
import posthog from 'posthog-js';
import { Observable, distinctUntilChanged, of } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Person properties attached to the PostHog profile on identify. */
interface PosthogUserProperties {
  email?: string;
  name?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class PosthogService {
  private readonly ngZone = inject(NgZone);

  constructor() {
    this.initPostHog();
  }

  private get enabled(): boolean {
    return !!environment.posthogKey;
  }

  private initPostHog() {
    if (!this.enabled) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      posthog.init(environment.posthogKey, {
        api_host: environment.posthogHost,
        debug: false,
        cross_subdomain_cookie: true,
        capture_pageview: 'history_change'
      });
    });
  }

  /**
   * Associates the current session with a known user so that person-scoped
   * feature flags (e.g. `email equals …` release conditions) can be evaluated.
   * Without this the session stays anonymous and such conditions never match.
   * `identify()` triggers a flag reload, which reactive {@link getFeatureFlag}
   * subscribers pick up automatically.
   */
  identifyUser(
    userId?: string | number | null,
    properties?: PosthogUserProperties
  ) {
    if (!this.enabled || userId == null || userId === '') {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.identify(String(userId), properties);
        if (properties?.email) {
          posthog.register({ email: properties.email });
        }
      } catch (error) {
        console.error('Failed to identify user in PostHog:', error);
      }
    });
  }

  /**
   * Associates the session with a company group so that group-scoped feature
   * flags (`$group_key equals …` release conditions) can be evaluated. Like
   * {@link identifyUser}, this triggers a flag reload.
   */
  setCompanyGroup(companyId?: string | number | null, companyName?: string) {
    if (!this.enabled || companyId == null || companyId === '') {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      try {
        posthog.group('company', String(companyId), {
          name: companyName ?? 'Unknown Company',
          id: companyId
        });
        posthog.register({ companyId });
        if (companyName) {
          posthog.register({ company: companyName });
        }
      } catch (error) {
        console.error('Failed to set PostHog company group:', error);
      }
    });
  }

  /**
   * Emits the current value of a feature flag and re-emits whenever PostHog
   * reloads its flags — notably after {@link identifyUser}/{@link setCompanyGroup}.
   * This keeps identity-gated flags in sync instead of caching the anonymous
   * result, so a flag that only becomes true once the user is identified will
   * update its subscribers rather than staying stuck on the initial `false`.
   */
  getFeatureFlag(key: string): Observable<boolean> {
    if (!this.enabled) {
      return of(true);
    }

    return new Observable<boolean>((observer) => {
      const emit = () => observer.next(posthog.isFeatureEnabled(key) ?? false);

      // Fires immediately with the flags known now (if already loaded) and
      // again on every subsequent reload.
      const unsubscribe = posthog.onFeatureFlags(emit);

      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    }).pipe(distinctUntilChanged());
  }
}
